import React, { useState, useEffect } from "react";
import { PageTitle, SSNFormField } from "../../components";
import {
  Skeleton,
  Table,
  Space,
  Button,
  Form,
  Input,
  Select,
  Modal,
  Tabs,
  message,
  DatePicker,
  Radio,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Routes, Route, useParams, Link } from "react-router-dom";
import type { DatePickerProps } from "antd";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import {
  usersApi,
  userClubsApi,
  ranksApi,
  teamsApi,
} from "../../_helpers/apiService";
import { useSelector } from "react-redux";

dayjs.extend(customParseFormat);

const { Option } = Select;
const { TabPane } = Tabs;

const dateFormat = "YYYY-MM-DD";

interface DataType {
  key?: string;
  last_name?: string;
  first_name?: string;
  team_id?: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

function Archer() {
  const [form] = Form.useForm();
  const [searchTeacher] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [userClubData, setUserClubData] = useState<any>(null);
  const [ranks, setRanks] = useState<any>({});
  const [teams, setTeams] = useState<any>({});
  const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] =
    useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  let { archerId } = useParams();

  useEffect(() => {
    fetchRanks();
    fetchTeams();
    if (archerId) {
      fetchArcherData();
    }
  }, [archerId]);

  const fetchRanks = async () => {
    try {
      const response = await ranksApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000,
      });
      const ranksObj: any = {};
      response.data.items.forEach((rank: any) => {
        ranksObj[rank.id] = rank;
      });
      setRanks(ranksObj);
    } catch (error: any) {
      console.error("Fetch ranks error:", error);
      message.error("Цолын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await teamsApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000,
      });
      const teamsObj: any = {};
      response.data.items.forEach((team: any) => {
        teamsObj[team.id] = team;
      });
      setTeams(teamsObj);
    } catch (error: any) {
      console.error("Fetch teams error:", error);
      message.error("Багийн мэдээлэл татахад алдаа гарлаа");
    }
  };

  const fetchArcherData = async () => {
    if (!archerId) return;

    setLoading(true);
    try {
      // Fetch user data
      const userResponse = await usersApi.getById(parseInt(archerId));
      const userData = userResponse.data;

      // Fetch user-club relationship for this club
      const userClubResponse = await userClubsApi.getAll({
        user_id: parseInt(archerId),
        club_id: CURRENT_CLUB_ID,
      });

      let clubMembershipData: any = {};
      if (
        userClubResponse.data.items &&
        userClubResponse.data.items.length > 0
      ) {
        clubMembershipData = userClubResponse.data.items[0];
        setUserClubData(clubMembershipData);
      }

      // Merge user data with club membership data
      const mergedData = {
        ...userData,
        // Club-specific fields from user_clubs table
        rank_id: clubMembershipData.rank_id || null,
        rank_date: clubMembershipData.rank_date || null,
        provincial_rank_id: clubMembershipData.provincial_rank_id || null,
        provincial_rank_date: clubMembershipData.provincial_rank_date || null,
        rate_id: clubMembershipData.rate_id || null,
        rate_date: clubMembershipData.rate_date || null,
        team_id: clubMembershipData.team_id || null,
        is_team_leader: clubMembershipData.is_team_leader || false,
      };

      setData(mergedData);

      // Helper function to safely parse dates
      const parseDate = (dateValue: any) => {
        if (!dateValue) return null;
        const parsed = dayjs(dateValue);
        return parsed.isValid() ? parsed : null;
      };

      // Set form values
      form.setFieldsValue({
        email: mergedData.email,
        phone_number:
          mergedData.phone_number?.replace(/^\+976\s?/, "") ||
          mergedData.phone_number,
        last_name: mergedData.last_name,
        first_name: mergedData.first_name,
        gender: mergedData.gender,
        birth_date: parseDate(mergedData.birth_date),
        ssn: mergedData.ssn,
        affiliation: mergedData.affiliation,
        team_id: mergedData.team_id,
        rank_id: mergedData.rank_id,
        rank_date: parseDate(mergedData.rank_date),
        provincial_rank_id: mergedData.provincial_rank_id,
        provincial_rank_date: parseDate(mergedData.provincial_rank_date),
        rate_id: mergedData.rate_id,
        rate_date: parseDate(mergedData.rate_date),
      });

      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching archer data:", error);
      message.error("Харваачийн мэдээлэл татахад алдаа гарлаа");
      setLoading(false);
    }
  };

  const onFinish = () => {
    setConfirmLoading(true);
    form
      .validateFields()
      .then(async (values) => {
        if (archerId) {
          try {
            let phoneNumber = values.phone_number;
            if (phoneNumber.length == 8) {
              phoneNumber = "+976" + phoneNumber;
            }
            // Update user data
            await usersApi.update(parseInt(archerId), {
              email: values.email,
              phone_number: phoneNumber,
              last_name: values.last_name,
              first_name: values.first_name,
              gender: values.gender,
              birth_date: values.birth_date
                ? dayjs(values.birth_date).format("YYYY-MM-DD")
                : undefined,
              ssn: values.ssn,
              affiliation: values.affiliation || null,
            });

            // Update or create user-club relationship
            const userClubUpdateData = {
              club_id: CURRENT_CLUB_ID,
              user_id: parseInt(archerId),
              rank_id: values.rank_id || undefined,
              rank_date: values.rank_date
                ? dayjs(values.rank_date).format("YYYY-MM-DD")
                : undefined,
              provincial_rank_id: values.provincial_rank_id || undefined,
              provincial_rank_date: values.provincial_rank_date
                ? dayjs(values.provincial_rank_date).format("YYYY-MM-DD")
                : undefined,
              rate_id: values.rate_id || undefined,
              rate_date: values.rate_date
                ? dayjs(values.rate_date).format("YYYY-MM-DD")
                : undefined,
              team_id: values.team_id || undefined,
              is_team_leader: values.is_team_leader || false,
            };

            if (userClubData && userClubData.id) {
              // Update existing user-club relationship
              await userClubsApi.update(userClubData.id, userClubUpdateData);
            } else {
              // Create new user-club relationship
              await userClubsApi.create(userClubUpdateData);
            }

            setConfirmLoading(false);
            message.success("Амжилттай.");

            // Refresh data
            fetchArcherData();
          } catch (error: any) {
            console.error("Error updating archer:", error);
            message.error("Харваач засахад алдаа гарлаа");
            setConfirmLoading(false);
          }
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setConfirmLoading(false);
      });
  };

  const handleResetPassword = () => {
    setResetPasswordLoading(true);
    resetPasswordForm
      .validateFields()
      .then(async (values) => {
        if (archerId) {
          try {
            await usersApi.resetPassword(
              parseInt(archerId),
              values.new_password
            );
            message.success("Нууц үг амжилттай солигдлоо");
            setIsResetPasswordModalVisible(false);
            resetPasswordForm.resetFields();
            setResetPasswordLoading(false);
          } catch (error: any) {
            console.error("Error resetting password:", error);
            message.error("Нууц үг солихыг алдаа гарлаа");
            setResetPasswordLoading(false);
          }
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setResetPasswordLoading(false);
      });
  };

  return (
    <>
      {data ? (
        <PageTitle
          loading={loading}
          title={`${data?.last_name} ${data?.first_name}`}
          description={"Харваачийн профайл"}
          right={
            <Button onClick={() => setIsResetPasswordModalVisible(true)}>
              Нууц үг солих
            </Button>
          }
          breadcrumbItems={[
            { link: "/archers", title: "Харваачид" },
            { title: `${data?.last_name} ${data?.first_name}` },
          ]}
        />
      ) : (
        ""
      )}

      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <Skeleton loading={loading} active>
                    <Form
                      {...formItemLayout}
                      form={form}
                      onFinish={() => onFinish()}
                    >
                      <Form.Item
                        label="Имэйл хаяг"
                        name="email"
                        rules={[
                          { required: true, message: "Имэйл хаяг оруулна уу!" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Утасны дугаар"
                        name="phone_number"
                        rules={[
                          {
                            required: true,
                            message: "Утасны дугаар оруулна уу!",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Овог"
                        name="last_name"
                        rules={[
                          { required: true, message: "Овог оруулна уу!" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Нэр"
                        name="first_name"
                        rules={[{ required: true, message: "Нэр оруулна уу!" }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item label="Хүйс" name="gender">
                        <Radio.Group value="male">
                          <Radio.Button value="male">Эрэгтэй</Radio.Button>
                          <Radio.Button value="female">Эмэгтэй</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item
                        label="Төрсөн огноо"
                        name="birth_date"
                        rules={[
                          {
                            required: false,
                            message: "Төрсөн огноо сонгоно уу!",
                          },
                        ]}
                      >
                        <DatePicker />
                      </Form.Item>
                      <Form.Item
                        label="Регистрийн дугаар"
                        name="ssn"
                        rules={[
                          {
                            required: true,
                            message: "Регистрийн дугаар оруулна уу!",
                          },
                        ]}
                      >
                        <SSNFormField userId={parseInt(archerId || "0")} />
                      </Form.Item>
                      <Form.Item
                        label="Харъяалал"
                        name="affiliation"
                        rules={[{ required: false }]}
                      >
                        <Input placeholder="Харъяалал (Жишээ: МУИС, МАК-23 гэх мэт)" />
                      </Form.Item>
                      <Form.Item
                        label="Баг"
                        name="team_id"
                        rules={[
                          { required: false, message: "Баг сонгоно уу!" },
                        ]}
                      >
                        <Select
                          placeholder="Баг"
                          allowClear
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {Object.values(teams)
                            .sort((a: any, b: any) =>
                              a.name > b.name ? 1 : -1
                            )
                            .map((team: any) => (
                              <Option key={team.id} value={team.id}>
                                {team.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="Улсын цол"
                        name="rank_id"
                        rules={[
                          { required: false, message: "Цол сонгоно уу!" },
                        ]}
                      >
                        <Select
                          placeholder="Улсын цол"
                          allowClear
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {Object.values(ranks)
                            .filter(
                              (rank: any) =>
                                !rank.is_rank &&
                                rank.club_id === CURRENT_CLUB_ID
                            )
                            .sort((a: any, b: any) =>
                              parseInt(a.order) > parseInt(b.order) ? 1 : -1
                            )
                            .map((rank: any) => (
                              <Option key={rank.id} value={rank.id}>
                                {rank.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="Улсын цол авсан огноо"
                        name="rank_date"
                        rules={[
                          {
                            required: false,
                            message: "Улсын цол авсан огноо сонгоно уу!",
                          },
                        ]}
                      >
                        <DatePicker />
                      </Form.Item>
                      <Form.Item
                        label="Аймгийн цол"
                        name="provincial_rank_id"
                        rules={[
                          { required: false, message: "Аймгийн цол сонгоно уу!" },
                        ]}
                      >
                        <Select
                          placeholder="Аймгийн цол"
                          allowClear
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {Object.values(ranks)
                            .filter(
                              (rank: any) =>
                                !rank.is_rank &&
                                rank.club_id === CURRENT_CLUB_ID
                            )
                            .sort((a: any, b: any) =>
                              parseInt(a.order) > parseInt(b.order) ? 1 : -1
                            )
                            .map((rank: any) => (
                              <Option key={rank.id} value={rank.id}>
                                {rank.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="Аймгийн цол авсан огноо"
                        name="provincial_rank_date"
                        rules={[
                          {
                            required: false,
                            message: "Аймгийн цол авсан огноо сонгоно уу!",
                          },
                        ]}
                      >
                        <DatePicker />
                      </Form.Item>
                      <Form.Item
                        label="Спортын зэрэг"
                        name="rate_id"
                        rules={[
                          {
                            required: false,
                            message: "Спортын зэрэг сонгоно уу!",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Спортын зэрэг"
                          allowClear
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {Object.values(ranks)
                            .filter((rank: any) => rank.is_rank)
                            .sort((a: any, b: any) =>
                              parseInt(a.order) > parseInt(b.order) ? 1 : -1
                            )
                            .map((rank: any) => (
                              <Option key={rank.id} value={rank.id}>
                                {rank.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="Спортын зэрэг авсан огноо"
                        name="rate_date"
                        rules={[
                          {
                            required: false,
                            message: "Спортын зэрэг авсан огноо сонгоно уу!",
                          },
                        ]}
                      >
                        <DatePicker />
                      </Form.Item>
                      <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={confirmLoading}
                        >
                          Хадгалах
                        </Button>
                      </Form.Item>
                    </Form>
                  </Skeleton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Нууц үг солих"
        open={isResetPasswordModalVisible}
        onOk={handleResetPassword}
        onCancel={() => {
          setIsResetPasswordModalVisible(false);
          resetPasswordForm.resetFields();
        }}
        confirmLoading={resetPasswordLoading}
        okText="Солих"
        cancelText="Болих"
      >
        <Form form={resetPasswordForm} layout="vertical">
          <Form.Item
            label="Шинэ нууц үг"
            name="new_password"
            rules={[
              { required: true, message: "Шинэ нууц үг оруулна уу!" },
              {
                min: 6,
                message: "Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой!",
              },
            ]}
          >
            <Input.Password placeholder="Шинэ нууц үг оруулна уу" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Archer;
